import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CameraIcon } from "@heroicons/react/24/outline";
import { IVehicle } from "../../../models/Vehicle";
import { ILead } from "../../../models/Lead";
import ClickToCopy from "../../shared/ClickToCopy";
import useStockNumber from "../../../hooks/useStockNumber";
import { ebayEngineSearchUrl, ebayMakeModelYearCategoryQuery } from "../../../utils/ebaySearchString";
import EbayLinkButton from "../../shared/Inputs/EbayLinkButton";
import PriceFormat from "../../../utils/priceFormat";

export default function TableVehicleDetails({
  vehicle,
  price,
  lead = null,
  showEbaySearch = false,
}: {
  vehicle: IVehicle | null;
  price?: number;
  lead?: ILead | null
  showEbaySearch?: boolean;
}) {
  const { t } = useTranslation("vehicles");
  const { getStockNumber } = useStockNumber();

  const ebaySearchQuery = useMemo(
    () =>
      vehicle
        ? ebayMakeModelYearCategoryQuery({
          dvlaYearOfManufacture: vehicle.dvlaYearOfManufacture,
          combinedMake: vehicle.combinedMake,
          modelRangeDescription: vehicle.modelRangeDescription,
        })
        : "",
    [vehicle]
  );

  const canSearchEbay =
    showEbaySearch &&
    !!vehicle?.combinedMake &&
    !!vehicle?.modelRangeDescription;

  const ebayEngineUrl = useMemo(
    () => (vehicle ? ebayEngineSearchUrl(vehicle) : null),
    [vehicle]
  );

  const canSearchEngineEbay = showEbaySearch && !!ebayEngineUrl;

  const imageSrc = vehicle?.onSitePhotoThumbnail
    ? `data:image/png;base64, ${vehicle.onSitePhotoThumbnail}`
    : vehicle?.vehicleImageUrl;

  if (!vehicle) {
    return <span className="text-gray-500 italic">{t("No Vehicle Data")}</span>;
  }

  return (
    <div className="flex gap-x-1 items-center relative">
      {/* price pill */}
      {price !== undefined && price !== null && price > 0 && (
        <span className="absolute top-0 left-0 bg-gray-200 border border-gray-400 text-gray-800 text-xs font-bold px-2 py-1 rounded-full">
          {price != null ? PriceFormat(price) : t("N/A")}
        </span>
      )}

      <span className="flex justify-center relative">
        {imageSrc ? (
          <img
            className="w-32 h-20 object-contain"
            src={imageSrc}
            alt={vehicle.vrm ?? t("Vehicle")}
          />
        ) : (
          <CameraIcon className="w-14 mx-10 text-gray-600" />
        )}
      </span>

      <div className="flex flex-col">
        <ClickToCopy title={t("VRM")} text={vehicle.vrm} />
        {vehicle.stockNumber && (
          <ClickToCopy title={t("Stock Number")} text={getStockNumber(vehicle) ?? ""} textClassName="text-xs font-semibold" />
        )}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="Make">{vehicle.combinedMake ?? ""}</span>
          {canSearchEbay && (
            <span onClick={(e) => e.stopPropagation()}>
              <EbayLinkButton query={ebaySearchQuery} />
            </span>
          )}
        </div>
        <span title={t("Model")}>{vehicle.modelRangeDescription ?? ""}</span>
        <ClickToCopy title={t("VIN")} text={vehicle.combinedVIN} textClassName="text-xs" />
        <ClickToCopy
          title={t("Engine Model Code")}
          text={vehicle.engineModelCode}
          textClassName="text-xs"
          className="w-full"
          middle={
            canSearchEngineEbay ? (
              <span onClick={(e) => e.stopPropagation()}>
                <EbayLinkButton
                  href={ebayEngineUrl!}
                  title={t("Search eBay for engine")}
                />
              </span>
            ) : undefined
          }
        />
        <span title={t("Transmission and Fuel Type")} className="text-xs">{vehicle.transmission ?? ""} {vehicle.fuelType ?? ""}</span>
      </div>

    </div>
  )
}